<template>
	<div class="container-md mh-100">
		<LayoutHeaderSection>
			<template #title>
				<LayoutTextSection text="Tus clientes asignados" :breadcrumb="true" />
			</template>
		</LayoutHeaderSection>
		<main class="pt-5 pb-10 pb-0 vstack gap-2">
			<CustomerCommonCompanyOptionList v-model="filters.company_id" v-if="showCompanies" />
			<CustomerCommonStatusOptionList ref="optionList" v-model="filters.status" :filters="filters" />
			<LayoutTableMain :results="results" :loading="loading" @goPage="goPage">
				<template #filters>
					<div class="hstack gap-2 flex-fill flex-sm-grow-0">
						<div class="input-group input-group-sm rounded-pill">
							<span class="input-group-text bg-white rounded-start-pill pe-0" id="basic-addon1"><i class="bi bi-search"></i></span>
							<input type="text" v-model="filters.search" class="form-control border-start-0 rounded-end-pill" placeholder="Buscar..." />
						</div>
						<button type="button" class="btn btn-sm btn-neutral rounded-pill flex-shrink-0" @click="filter">+ Filtros</button>
					</div>
				</template>
				<template #body>
					<CustomerTableRow v-for="data in results?.data" v-bind:key="data.id" :customer="data" :selectable="true" />
				</template>
			</LayoutTableMain>
		</main>

		<CommonModalFilter ref="modalFilter" @success="setFilters">
			<div class="row g-5" v-if="Object.keys(groupsOptions).length > 1">
				<div class="col-12">
					<CommonFormInputSelect v-model="extras.group_id" title="Grupo" placeholder="No filtrar" :options="groupsOptions" />
				</div>
			</div>
			<div class="row g-5">
				<div class="col-12">
					<CommonFormInputSelect v-model="extras.register_online" title="Tipo de registro" placeholder="No filtrar" :options="{ 1: 'En tienda online', 0: 'En tienda física' }" />
				</div>
			</div>
			<div class="row g-5">
				<div class="col-12">
					<CommonFormInputSelect v-model="extras.birthdate" title="Cumpleaños" placeholder="No filtrar" :options="{ 0: 'Hoy', 1: 'Entre hoy y mañana', 7: 'Hasta en 1 semana', 30: 'Hasta en 1 mes' }" />
				</div>
			</div>
			<div class="row g-5">
				<div class="col-6">
					<CommonFormInputNumber v-model="extras.total_buy.min" title="Compra total desde" placeholder="Ingrese el mínimo" />
				</div>
				<div class="col-6">
					<CommonFormInputNumber v-model="extras.total_buy.max" title="Hasta" subtitle="Opcional" placeholder="Ingrese el máximo" />
				</div>
			</div>
			<div class="row g-5">
				<div :class="{ 'col-6': !!extras.last_buy.type, 'col-12': !extras.last_buy.type }">
					<CommonFormInputSelect v-model="extras.last_buy.type" title="Última compra" placeholder="No filtrar" :options="{ less: 'Hace menos de', more: 'Hace más de' }" />
				</div>
				<div class="col-6" v-if="extras.last_buy.type">
					<CommonFormInputSelect v-model="extras.last_buy.months" title="Tiempo" :options="months" />
				</div>
			</div>
		</CommonModalFilter>
	</div>
</template>

<script>
	import { ref, reactive, computed, watch, onMounted, onUnmounted, getCurrentInstance, inject } from 'vue';
	import { useRoute } from 'vue-router';
	import router from '@/router';
	import store from '@/store';
	import _ from 'lodash';
	import composableForm from '@/composables/form';
	import CustomerRepository from '@/repositories/modules/Customer';
	import GroupRepository from '@/repositories/modules/Group';

	export default {
		setup() {
			const { loading, catchError } = composableForm();
			const instance = getCurrentInstance();
			const route = useRoute();
			const $env = inject('$env');
			const showCompanies = computed(() => _.size(_.filter(store.getters['auth/user'].companies, company => company.pivot.roles.includes('seller'))) > 1);
			const statusesOptions = computed(() => _.mapValues($env.MODEL.CustomerUser.statuses, (role) => role.title));
			const optionList = ref(null);
			const groups = ref([]);
			const groupsOptions = computed(() => {
				let data = _.mapValues(_.keyBy(groups.value, 'id'), (group) => {
					return `${group.name} de ${group.company.name}`;
				});

				data[0] = 'Sin grupo asinado';

				return data;
			});
			const results = ref({});

			const extras = reactive({
				group_id: null,
				register_online: null,
				birthdate: route.query.birthdate ?? null,
				last_buy: {
					type: null,
					months: '1'
				},
				total_buy: {
					min: null,
					max: null
				}
			});

			const filters = reactive({
				search: null,
				company_id: route.query.company_id,
				status: route.query.status,
				extras: _.cloneDeep(extras),
				take: 20,
				page: 1
			});

			const months = {};
			for(var i = 1; i <= 12; i++) months[i] = (i == 1) ? '1 Mes' : `${i} Meses`;

			const getResults = async () => {
				loading.value = true;

				await CustomerRepository.index(filters).then((response) => {
					results.value = response.data;
				}).catch((e) => {
					catchError(e, true);
					router.push({ name: 'home' });
				});

				loading.value = false;
			}

			const goPage = (page) => {
				filters.page = page;
				getResults();
			}

			const modalFilter = ref(null);
			const filter = () => modalFilter.value.show();
			const setFilters = () => {
				filters.extras = extras;
				goPage(1);
			}

			const timeout = ref(null);
			watch(() => [filters.search, filters.company_id, filters.status], () => {
				if(timeout.value) clearTimeout(timeout.value);
				timeout.value = setTimeout(() => goPage(1), 1000);
			});

			watch(() => filters.company_id, () => {
				filters.status = null;
				optionList.value.getResults();
			});

			onMounted(async () => {
				getResults();

				try {
					const [ groupResponse ] = await Promise.all([
						GroupRepository.list()
					]);

					if(groupResponse?.data !== undefined) {
						groups.value = groupResponse.data;
					}
				}catch(e) {
					catchError(e, true);
				}

				if(instance) instance.appContext.config.globalProperties.$customerRefresh = () => {
					getResults();
					optionList.value.getResults();
				}
			});

			onUnmounted(() => {
				if(instance && instance.appContext.config.globalProperties.$customerRefresh) {
					delete instance.appContext.config.globalProperties.$customerRefresh;
				}
			});

			return { loading, showCompanies, statusesOptions, groupsOptions, filters, extras, months, optionList, results, goPage, modalFilter, filter, setFilters, getResults };
		}
	}
</script>